import React from "react";
import { motion } from "framer-motion";

/**
 * Word-by-word blur-in reveal. Pass a string and it will be split on spaces.
 * Preserves inline HTML if you pass React children — those are revealed as one block.
 */
export default function BlurReveal({
  text,
  children,
  as: Tag = "h2",
  className = "",
  delay = 0,
  stagger = 0.04,
  duration = 0.9,
  inView = false,
  testId,
}) {
  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (prefersReduced) {
    if (children) return <Tag className={className} data-testid={testId}>{children}</Tag>;
    return <Tag className={className} data-testid={testId}>{text}</Tag>;
  }

  const common = {
    initial: { opacity: 0, y: 18, filter: "blur(14px)" },
    whileInView: inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : undefined,
    animate: inView ? undefined : { opacity: 1, y: 0, filter: "blur(0px)" },
    viewport: inView ? { once: true, margin: "-10%" } : undefined,
  };

  if (children) {
    return (
      <motion.div
        {...common}
        transition={{ duration, delay, ease: [0.16, 1, 0.3, 1] }}
        className={className}
        data-testid={testId}
      >
        {children}
      </motion.div>
    );
  }

  const words = (text || "").split(/(\s+)/);

  return (
    <Tag className={className} data-testid={testId}>
      {words.map((w, i) =>
        /^\s+$/.test(w) ? (
          <span key={`s-${i}`}>{w}</span>
        ) : (
          <motion.span
            key={`w-${i}`}
            initial={{ opacity: 0, y: 20, filter: "blur(12px)" }}
            whileInView={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : undefined}
            animate={inView ? undefined : { opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={inView ? { once: true, margin: "-10%" } : undefined}
            transition={{
              duration,
              delay: delay + i * stagger,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="inline-block will-change-[transform,filter,opacity]"
          >
            {w}
          </motion.span>
        )
      )}
    </Tag>
  );
}
